import { useCallback, useState } from "react";

import { Copy, Link, Mail, Send, Share } from "lucide-react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import {
    Dialog,
    DialogTrigger,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Separator } from "~/components/ui/separator";
import { useApiClient } from "~/hooks/use-api-client";
import { useSessionSharingToken } from "~/hooks/use-session-sharing-token";
import { trackEvent } from "~/lib/analytics";
import { getSessionSharingUrl } from "~/lib/utils";

import { Loader } from "../ui/loader";
import { P } from "../ui/typography";

// AIDEV-NOTE: Unified share dialog used by both clients and practitioners.
// showLinkOption=true for practitioners (email + link), false for clients (email only).
export const UnifiedShareDialog: React.FC<{
    sessionId: string;
    showLinkOption: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}> = ({ sessionId, showLinkOption, open: controlledOpen, onOpenChange }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
    const setOpen = onOpenChange || setInternalOpen;

    const [recipientEmail, setRecipientEmail] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);
    const apiClient = useApiClient();

    // Link sharing state (only used when showLinkOption=true)
    const [expiration, setExpiration] = useState<"30days" | "never">("30days");
    const {
        sharingToken,
        createSharingToken,
        isLoading: isLoadingToken,
    } = useSessionSharingToken(sessionId);

    const copyLink = useCallback(() => {
        if (!sharingToken) return;
        const link = getSessionSharingUrl(sessionId, sharingToken);
        navigator.clipboard.writeText(link);
        toast.success("Link copied to clipboard");
        trackEvent("copied session share link", {});
    }, [sharingToken, sessionId]);

    const handleSendEmail = useCallback(async () => {
        if (!recipientEmail.trim()) return;

        setIsSendingEmail(true);
        try {
            const response = await apiClient.post("/session/share-via-email", {
                sessionId,
                recipientEmail: recipientEmail.trim(),
            });

            if (response.ok) {
                toast.success(`Session shared with ${recipientEmail}`);
                trackEvent("shared session via email", {
                    sessionId,
                    recipientEmail,
                });
                setRecipientEmail("");
            } else {
                const data = await response.json();
                toast.error(data.error || "Failed to send email");
            }
        } catch {
            toast.error("Failed to send email");
        } finally {
            setIsSendingEmail(false);
        }
    }, [apiClient, sessionId, recipientEmail]);

    return (
        <Dialog
            open={open}
            onOpenChange={setOpen}
            analyticsId="Share session notes"
        >
            {controlledOpen === undefined && (
                <DialogTrigger asChild>
                    <Button
                        variant="default"
                        size="default"
                        className="flex items-center gap-2 text-sm print:hidden"
                    >
                        <Share className="size-4" />
                        Share
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>Share Session Notes</DialogTitle>
                    <DialogDescription className="text-md pt-2 text-brand-forest-800">
                        Share your session notes via email.
                    </DialogDescription>
                </DialogHeader>

                {/* Email sharing section */}
                <div className="space-y-3">
                    <div className="flex gap-2">
                        <Input
                            type="email"
                            placeholder="Enter email address"
                            value={recipientEmail}
                            onChange={(e) => setRecipientEmail(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    handleSendEmail();
                                }
                            }}
                        />
                        <Button
                            onClick={handleSendEmail}
                            disabled={!recipientEmail.trim() || isSendingEmail}
                            isLoading={isSendingEmail}
                            size="default"
                        >
                            <Send className="size-4" />
                        </Button>
                    </div>
                </div>

                {/* Link sharing section (practitioners only) */}
                {showLinkOption && (
                    <>
                        <Separator className="my-2" />
                        <div className="space-y-3">
                            <P className="text-sm font-medium">
                                Or create a public link
                            </P>
                            <RadioGroup
                                value={expiration}
                                onValueChange={(value: "30days" | "never") => {
                                    setExpiration(value);
                                    if (sharingToken) {
                                        createSharingToken(value === "never");
                                    }
                                }}
                                className="flex gap-4"
                            >
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem
                                        value="30days"
                                        id="30days"
                                    />
                                    <Label
                                        htmlFor="30days"
                                        className="cursor-pointer text-sm font-normal"
                                    >
                                        Expires in 30 days
                                    </Label>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <RadioGroupItem value="never" id="never" />
                                    <Label
                                        htmlFor="never"
                                        className="cursor-pointer text-sm font-normal"
                                    >
                                        Never expires
                                    </Label>
                                </div>
                            </RadioGroup>
                            <div>
                                {sharingToken || isLoadingToken ? (
                                    <Button
                                        variant="outline"
                                        onClick={copyLink}
                                        isLoading={isLoadingToken}
                                        className="flex items-center gap-2"
                                    >
                                        <Copy className="size-4" />
                                        Copy Link
                                    </Button>
                                ) : (
                                    <Button
                                        variant="outline"
                                        onClick={() =>
                                            createSharingToken(
                                                expiration === "never",
                                            )
                                        }
                                        className="flex items-center gap-2"
                                    >
                                        <Link className="size-4" />
                                        Create Link
                                    </Button>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};
